import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  marketplace as marketplaceApi,
  entities,
  media,
} from "../services/api";
import { formatDate, formatCurrency } from "../utils/dateUtils";
import {
  BackIcon,
  ClipboardListIcon,
  SearchIcon,
  RefreshIcon,
  LinkIcon,
  CheckIcon,
  CloseIcon,
  PlusIcon,
  EyeIcon,
} from "../components/Icons";
import DataTable, { RowActions } from "../components/DataTable";
import { toast } from "../components/Toast";
import OfferUpListingModal from "../components/OfferUpListingModal";
import EbayListingModal from "../components/EbayListingModal";

function ItemsListed() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [linkedFilter, setLinkedFilter] = useState("unlinked");
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalListedValue: 0,
  });

  // Link modal state
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkingItem, setLinkingItem] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [linking, setLinking] = useState(false);

  // OfferUp listing modal state
  const [showOfferupModal, setShowOfferupModal] = useState(false);
  const [offerupListing, setOfferupListing] = useState(null);

  // eBay listing modal state
  const [showEbayModal, setShowEbayModal] = useState(false);
  const [ebayProduct, setEbayProduct] = useState(null);

  const searchDebounceRef = useRef(null);
  const mouseDownTargetRef = useRef(null);

  const loadListings = useCallback(async () => {
    try {
      setLoading(true);
      const [listingsRes, statsRes] = await Promise.all([
        marketplaceApi.list(),
        marketplaceApi.stats(),
      ]);
      setItems(listingsRes.data.data || []);
      setStats(
        statsRes.data.data || { total: 0, active: 0, totalListedValue: 0 },
      );
      setError(null);
    } catch (err) {
      console.error("Failed to load listings:", err);
      setError("Couldn't load listings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  // Link modal functions
  const openLinkModal = (item) => {
    setLinkingItem(item);
    setProductSearch(item.title || "");
    setProductResults([]);
    setLinkModalOpen(true);
    // Auto-search with listing title (immediate, no debounce)
    handleProductSearch(item.title || "", true);
  };

  const closeLinkModal = () => {
    setLinkModalOpen(false);
    setLinkingItem(null);
    setProductSearch("");
    setProductResults([]);
  };

  const handleProductSearch = (query, immediate = false) => {
    setProductSearch(query);

    // Clear any pending debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (!query || query.length < 2) {
      setProductResults([]);
      setSearchingProducts(false);
      return;
    }

    const doSearch = async () => {
      setSearchingProducts(true);
      try {
        const res = await entities.list({
          entity_type: "product",
          search: query,
          limit: 10,
        });
        // API returns array directly, not wrapped in data
        setProductResults(
          Array.isArray(res.data) ? res.data : res.data.data || [],
        );
      } catch (err) {
        console.error("Failed to search products:", err);
        setProductResults([]);
      } finally {
        setSearchingProducts(false);
      }
    };

    if (immediate) {
      doSearch();
    } else {
      setSearchingProducts(true);
      searchDebounceRef.current = setTimeout(doSearch, 300);
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleLinkProduct = async (product) => {
    if (!linkingItem) return;

    setLinking(true);
    try {
      await marketplaceApi.update(linkingItem.id, { entity_id: product.id });
      await loadListings();
      closeLinkModal();
    } catch (err) {
      console.error("Failed to link product:", err);
      toast.error(
        "Failed to link product: " +
          (err.response?.data?.error || err.message || "Unknown error"),
      );
    } finally {
      setLinking(false);
    }
  };

  const handleCreateFromListing = async () => {
    if (!linkingItem) return;

    setLinking(true);
    try {
      // Get photo URL from listing_data if available
      const listingData =
        typeof linkingItem.listing_data === "string"
          ? JSON.parse(linkingItem.listing_data)
          : linkingItem.listing_data;
      const photoUrl = listingData?.photo;

      // Create the product
      const productData = {
        entity_type: "product",
        name: linkingItem.title,
        description: linkingItem.description || "",
        list_sale_price: linkingItem.price,
        condition: linkingItem.condition,
      };

      const res = await entities.create(productData);
      const newProduct = res.data;

      // Download and attach the image if we have a photo URL
      if (photoUrl) {
        try {
          await media.downloadFromUrl(photoUrl, newProduct.id);
        } catch (imgErr) {
          console.error("Failed to download image:", imgErr);
          // Continue anyway - product was created
        }
      }

      // Link the listing to the new product
      await marketplaceApi.update(linkingItem.id, { entity_id: newProduct.id });

      await loadListings();
      closeLinkModal();
    } catch (err) {
      console.error("Failed to create product:", err);
      toast.error(
        "Failed to create product: " +
          (err.response?.data?.error || err.message || "Unknown error"),
      );
    } finally {
      setLinking(false);
    }
  };

  const getDaysListed = (listedDate) => {
    if (!listedDate) return 0;
    const listed = new Date(listedDate);
    const today = new Date();
    const diffTime = Math.abs(today - listed);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      active: "badge-success",
      pending: "badge-warning",
      expired: "badge-danger",
      sold: "badge-product",
      deleted: "badge-danger",
    };
    return statusColors[status] || "";
  };

  const formatMarketplace = (marketplace) => {
    const names = {
      offerup: "OfferUp",
      ebay: "eBay",
      facebook: "Facebook",
      craigslist: "Craigslist",
      mercari: "Mercari",
    };
    return names[marketplace] || marketplace;
  };

  // Filter and sort items
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      !searchQuery ||
      item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.entity_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.marketplace?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlatform =
      platformFilter === "all" || item.marketplace === platformFilter;
    const matchesStatus =
      statusFilter === "all" || item.status === statusFilter;
    const matchesLinked =
      linkedFilter === "all" ||
      (linkedFilter === "linked" && item.entity_id) ||
      (linkedFilter === "unlinked" && !item.entity_id);
    return matchesSearch && matchesPlatform && matchesStatus && matchesLinked;
  });

  // Get unique platforms for filter
  const platforms = [...new Set(items.map((item) => item.marketplace))];

  // Column definitions
  const columns = [
    {
      field: "title",
      header: "Item",
      render: (_, item) => (
        <span style={{ fontWeight: 500 }}>
          {item.title || item.entity_name || "Untitled"}
        </span>
      ),
      getSortValue: (item) => (item.title || "").toLowerCase(),
    },
    {
      field: "marketplace",
      header: "Platform",
      render: (value) => (
        <span className="badge">{formatMarketplace(value)}</span>
      ),
      getSortValue: (item) => (item.marketplace || "").toLowerCase(),
    },
    {
      field: "price",
      header: "Price",
      align: "right",
      render: (value) => formatCurrency(value || 0),
      getSortValue: (item) => parseFloat(item.price) || 0,
    },
    {
      field: "status",
      header: "Status",
      align: "center",
      render: (value) => (
        <span className={`badge ${getStatusBadge(value)}`}>{value}</span>
      ),
      getSortValue: (item) => (item.status || "").toLowerCase(),
    },
    {
      field: "posted_at",
      header: "Listed",
      render: (value) => formatDate(value),
      getSortValue: (item) => new Date(item.posted_at).getTime(),
    },
    {
      field: "days",
      header: "Days",
      align: "right",
      sortable: false,
      render: (_, item) => getDaysListed(item.posted_at),
    },
    {
      field: "linked",
      header: "Linked",
      align: "center",
      sortable: false,
      render: (_, item) =>
        item.entity_id ? (
          <span style={{ color: "var(--success, #10b981)" }}>
            <CheckIcon size={16} />
          </span>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              openLinkModal(item);
            }}
            className="btn btn-secondary btn-sm"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <LinkIcon size={14} />
            Link
          </button>
        ),
    },
  ];

  // Row actions
  const rowActions = (item) => {
    const actions = [];
    if (item.external_url) {
      actions.push({
        label: "View Listing",
        icon: EyeIcon,
        onClick: () => window.open(item.external_url, "_blank"),
      });
    }
    if (item.entity_id) {
      actions.push({
        label: "List on OfferUp",
        onClick: () => {
          // Fetch the linked product to get its details
          entities
            .get(item.entity_id)
            .then(({ data: product }) => {
              setOfferupListing(product);
              setShowOfferupModal(true);
            })
            .catch((err) => {
              console.error("Failed to load product:", err);
              toast.error("Failed to load the linked product");
            });
        },
      });
    }
    if (item.entity_id) {
      actions.push({
        label: "List on eBay",
        onClick: () => {
          // Fetch the linked product so the modal can prefill from it
          entities
            .get(item.entity_id)
            .then(({ data: product }) => {
              setEbayProduct(product);
              setShowEbayModal(true);
            })
            .catch((err) => {
              console.error("Failed to load product:", err);
              toast.error("Failed to load the linked product");
            });
        },
      });
    }
    return <RowActions actions={actions} />;
  };

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Items Listed</h1>
            <p className="header-subtitle">
              {stats.active} active listing{stats.active !== 1 ? "s" : ""}
            </p>
          </div>
          <div
            className="header-actions-right"
            style={{ display: "flex", gap: "var(--spacing-sm)" }}
          >
            <button
              onClick={loadListings}
              className="btn btn-ghost btn-icon"
              title="Refresh listings"
              disabled={loading}
            >
              <RefreshIcon size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "var(--spacing-md)",
            marginBottom: "var(--spacing-lg)",
          }}
        >
          <div className="card" style={{ padding: "var(--spacing-lg)" }}>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                textTransform: "uppercase",
                marginBottom: "var(--spacing-xs)",
              }}
            >
              Total Listed Value
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700" }}>
              {formatCurrency(stats.totalListedValue || 0)}
            </div>
          </div>
          <div className="card" style={{ padding: "var(--spacing-lg)" }}>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                textTransform: "uppercase",
                marginBottom: "var(--spacing-xs)",
              }}
            >
              Active Listings
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700" }}>
              {stats.active}
            </div>
          </div>
          <div className="card" style={{ padding: "var(--spacing-lg)" }}>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                textTransform: "uppercase",
                marginBottom: "var(--spacing-xs)",
              }}
            >
              Platforms
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700" }}>
              {platforms.length}
            </div>
          </div>
        </div>

        {/* Filters above DataTable */}
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-sm)",
            marginBottom: "var(--spacing-md)",
            flexWrap: "wrap",
          }}
        >
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input"
            style={{ width: "auto", minWidth: "120px", margin: 0 }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="sold">Sold</option>
            <option value="pending">Pending</option>
            <option value="expired">Expired</option>
            <option value="deleted">Deleted</option>
          </select>
          <select
            value={linkedFilter}
            onChange={(e) => setLinkedFilter(e.target.value)}
            className="input"
            style={{ width: "auto", minWidth: "120px", margin: 0 }}
          >
            <option value="all">All Items</option>
            <option value="linked">Linked</option>
            <option value="unlinked">Unlinked</option>
          </select>
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="input"
            style={{ width: "auto", minWidth: "130px", margin: 0 }}
          >
            <option value="all">All Platforms</option>
            {platforms.map((platform) => (
              <option key={platform} value={platform}>
                {formatMarketplace(platform)}
              </option>
            ))}
          </select>
        </div>

        {/* DataTable — sorting is client-side over the loaded listings */}
        {error && items.length === 0 ? (
          <div
            className="card text-center"
            style={{ padding: "var(--spacing-2xl)" }}
          >
            <p
              style={{
                marginBottom: "var(--spacing-md)",
                color: "var(--text-secondary)",
              }}
            >
              {error}
            </p>
            <button onClick={loadListings} className="btn btn-primary">
              Retry
            </button>
          </div>
        ) : (
          <DataTable
            data={filteredItems}
            loading={loading}
            columns={columns}
            searchable
            searchPlaceholder="Search listings..."
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            defaultSortField="posted_at"
            defaultSortDirection="desc"
            onRowClick={(item) =>
              item.entity_id
                ? navigate(`/entity/${item.entity_id}`)
                : openLinkModal(item)
            }
            rowActions={rowActions}
            emptyIcon={ClipboardListIcon}
            emptyTitle={
              items.length > 0 ? "No matching listings" : "No listings yet"
            }
            emptySubtitle={
              items.length > 0
                ? "Adjust the status, linked, or platform filters above"
                : "Items you list on marketplaces will appear here"
            }
          />
        )}
      </div>

      {/* Link Product Modal */}
      {linkModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "var(--spacing-lg)",
          }}
          onMouseDown={(e) => {
            mouseDownTargetRef.current = e.target;
          }}
          onClick={(e) => {
            // Only close if mousedown and mouseup were both on the backdrop
            if (
              e.target === e.currentTarget &&
              mouseDownTargetRef.current === e.target
            ) {
              closeLinkModal();
            }
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "500px",
              maxHeight: "80vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              background: "var(--bg-primary, #fff)",
              borderRadius: "var(--radius-lg, 12px)",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "var(--spacing-lg)",
                borderBottom: "1px solid var(--border-color)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0 }}>Link to Product</h3>
              <button
                onClick={closeLinkModal}
                className="btn btn-ghost btn-icon"
              >
                <CloseIcon size={20} />
              </button>
            </div>

            <div style={{ padding: "var(--spacing-lg)" }}>
              <p
                style={{
                  marginBottom: "var(--spacing-md)",
                  color: "var(--text-secondary)",
                }}
              >
                Link "<strong>{linkingItem?.title}</strong>" to a product in
                your inventory:
              </p>

              <div
                style={{
                  position: "relative",
                  marginBottom: "var(--spacing-md)",
                }}
              >
                <SearchIcon
                  size={18}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--text-muted)",
                  }}
                />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={(e) => handleProductSearch(e.target.value)}
                  className="input"
                  style={{ paddingLeft: "40px", width: "100%" }}
                  autoFocus
                />
              </div>
            </div>

            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: "0 var(--spacing-lg) var(--spacing-lg)",
              }}
            >
              {searchingProducts ? (
                <div
                  style={{ textAlign: "center", padding: "var(--spacing-lg)" }}
                >
                  <div className="spinner"></div>
                </div>
              ) : productResults.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "var(--spacing-lg)",
                  }}
                >
                  {productSearch.length < 2 ? (
                    <span style={{ color: "var(--text-muted)" }}>
                      Type to search for products
                    </span>
                  ) : (
                    <div>
                      <p
                        style={{
                          color: "var(--text-muted)",
                          marginBottom: "var(--spacing-md)",
                        }}
                      >
                        No products found
                      </p>
                      <button
                        onClick={handleCreateFromListing}
                        disabled={linking}
                        className="btn btn-primary"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <PlusIcon size={16} />
                        {linking ? "Creating..." : "Create New Product"}
                      </button>
                      <p
                        style={{
                          fontSize: "0.75rem",
                          color: "var(--text-muted)",
                          marginTop: "var(--spacing-sm)",
                        }}
                      >
                        Creates a product from this listing's details
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--spacing-sm)",
                  }}
                >
                  {productResults.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleLinkProduct(product)}
                      disabled={linking}
                      className="btn"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--spacing-md)",
                        padding: "var(--spacing-md)",
                        textAlign: "left",
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        justifyContent: "flex-start",
                      }}
                    >
                      {product.image_path ? (
                        <img
                          src={media.getImageUrl(product.image_path)}
                          alt={product.name}
                          style={{
                            width: "48px",
                            height: "48px",
                            objectFit: "cover",
                            borderRadius: "var(--radius-sm)",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            background: "var(--bg-hover)",
                            borderRadius: "var(--radius-sm)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--text-muted)",
                          }}
                        >
                          ?
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {product.name}
                        </div>
                        <div
                          style={{
                            fontSize: "0.875rem",
                            color: "var(--text-secondary)",
                          }}
                        >
                          {product.sku && <span>SKU: {product.sku}</span>}
                          {product.retail_price && (
                            <span
                              style={{
                                marginLeft: product.sku
                                  ? "var(--spacing-sm)"
                                  : 0,
                              }}
                            >
                              {formatCurrency(product.retail_price)}
                            </span>
                          )}
                        </div>
                      </div>
                      <LinkIcon
                        size={16}
                        style={{ color: "var(--text-muted)" }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* OfferUp Listing Modal */}
      {showOfferupModal && (
        <OfferUpListingModal
          product={offerupListing}
          onClose={() => {
            setShowOfferupModal(false);
            setOfferupListing(null);
          }}
          onSuccess={() => {
            setShowOfferupModal(false);
            setOfferupListing(null);
            loadListings();
          }}
        />
      )}

      {/* eBay Listing Modal */}
      {showEbayModal && ebayProduct && (
        <EbayListingModal
          product={ebayProduct}
          onClose={() => {
            setShowEbayModal(false);
            setEbayProduct(null);
          }}
          onSuccess={() => {
            setShowEbayModal(false);
            setEbayProduct(null);
            loadListings();
          }}
        />
      )}
    </div>
  );
}

export default ItemsListed;
